import { useEffect, useRef } from "react";
import { X, Edit2, Lock } from "lucide-react";
import { cn } from "@/utils/cn";
import { AddMeetingForm } from "./AddMeetingForm";
import type { FiscalYearResponse } from "@/types/draftCalendar";

interface MeetingFlyoutProps {
  meetingId: number;
  fiscalYear: FiscalYearResponse | undefined;
  businesses: Record<string, string[]>;
  meetingTypes: Record<string, string[]>;
  attendeeLevels: string[];
  locations: string[];
  duDeclinedReasons?: string[];
  isViewOnly: boolean;
  lockedBy: string | null;
  canEdit: boolean;
  isBusinessApprover?: boolean;
  canEditSignOffStatus?: boolean;
  onEdit: () => void;
  onSaved: () => void;
  onClose: () => void;
}

/**
 * Right-side flyout panel for viewing / editing a draft meeting.
 * Wraps AddMeetingForm with a title bar, lock indicator, and Edit/Cancel controls.
 * Mirrors Blazor CreateDraftEvent popup pattern.
 */
export function MeetingFlyout({
  meetingId,
  fiscalYear,
  businesses,
  meetingTypes,
  attendeeLevels,
  locations,
  duDeclinedReasons = [],
  isViewOnly,
  lockedBy,
  canEdit,
  isBusinessApprover = false,
  canEditSignOffStatus = false,
  onEdit,
  onSaved,
  onClose
}: MeetingFlyoutProps) {
  const isNew = meetingId === 0;
  const title = isNew ? "Add Meeting" : isViewOnly ? "Meeting Details" : "Edit Meeting";
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus into the panel when it opens.
  useEffect(() => {
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, []);

  // Trap focus within the dialog panel.
  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const focusables = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <>
      {/* Backdrop — presentational, not interactive for AT */}
      <div
        className="fixed inset-0 z-50 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Dialog panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="meeting-flyout-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 ml-auto flex h-full w-[480px] flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handlePanelKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="meeting-flyout-title" className="text-base font-semibold text-slate-900">
            {title}
          </h2>
          <div className="flex items-center gap-2">
            {isViewOnly && !isNew && canEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="flex items-center gap-1 rounded border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50"
              >
                <Edit2 className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="text-slate-500 hover:text-slate-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Lock banner */}
        {lockedBy && isViewOnly && !isNew && (
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            <Lock className="h-3.5 w-3.5 flex-shrink-0" />
            Currently being edited by <strong className="ml-1">{lockedBy}</strong>
          </div>
        )}

        {/* Form */}
        <div className="flex flex-1 overflow-hidden">
          <AddMeetingForm
            meetingId={meetingId}
            fiscalYear={fiscalYear}
            businesses={businesses}
            meetingTypes={meetingTypes}
            attendeeLevels={attendeeLevels}
            locations={locations}
            duDeclinedReasons={duDeclinedReasons}
            isViewOnly={isViewOnly}
            isBusinessApprover={isBusinessApprover}
            canEditSignOffStatus={canEditSignOffStatus}
            onSaved={onSaved}
            onCancel={onClose}
          />
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-200 px-4 py-3">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              {isViewOnly ? "Close" : "Cancel"}
            </button>
            {!isViewOnly && (
              <button
                type="submit"
                form="meeting-form"
                className={cn(
                  "border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white",
                  "hover:bg-slate-800 disabled:opacity-60"
                )}
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
